import { Alert, Button, Group, Loader, Modal, Text } from '@mantine/core';
import { IconAlertTriangle, IconDeviceFloppy } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classes from './Forms.module.css';
import {
  inspectPdfForm,
  openPdfForm,
  PDF_FORM_RENDER_OPTIONS,
  type PdfFormDocument,
  type PdfjsModule,
  savePdfForm,
} from './pdf-form.js';
import { formErrorText } from './status.js';

export interface PdfFormFillerProps {
  opened: boolean;
  /** Şablon PDF'in adresi (gemide `felix-file://file/<id>`). */
  url: string | null;
  /** Pencerenin başlığı: kayıt no ve form. */
  title: string;
  onClose: () => void;
  /** Doldurulan baytlar (`saveDocument()`); kayda ekleme çağıranda. */
  onSave: (bytes: Uint8Array) => Promise<void>;
  /** pdf.js yükleyicisi (test için değiştirilebilir). */
  loadPdfjs?: () => Promise<PdfjsModule>;
}

type State =
  | { kind: 'loading' }
  | { kind: 'error'; code: string }
  | { kind: 'ready'; doc: PdfFormDocument; pdfjs: PdfjsModule };

/** CSS px / PDF noktası (96 / 72): %100'de A4 genişliği 794 px. */
const PDF_SCALE = 96 / 72;

const defaultLoader = async () =>
  (await import('../documents/viewer-engines.js')).pdfjsLib() as unknown as Promise<PdfjsModule>;

/** Bağlantı hizmeti: formda bağlantı ve betik yok; pdf.js'in istediği yüzey boş işlemlerle. */
const LINK_SERVICE = {
  eventBus: null,
  externalLinkEnabled: false,
  getDestinationHash: () => '#',
  getAnchorUrl: () => '#',
  addLinkAttributes: () => undefined,
  goToDestination: async () => undefined,
  executeNamedAction: () => undefined,
  executeSetOCGState: () => undefined,
  getAttachmentContent: async () => null,
};

async function renderPages(
  container: HTMLElement,
  pdfjs: PdfjsModule,
  doc: PdfFormDocument,
  isCancelled: () => boolean,
) {
  container.replaceChildren();
  for (let number = 1; number <= doc.numPages; number += 1) {
    const page = await doc.getPage(number);
    if (isCancelled()) return;
    const viewport = page.getViewport({ scale: PDF_SCALE });
    const holder = globalThis.document.createElement('div');
    holder.className = classes.pdfPage ?? '';
    holder.style.width = `${viewport.width}px`;
    holder.style.height = `${viewport.height}px`;
    holder.style.setProperty('--scale-factor', String(PDF_SCALE));
    holder.style.setProperty('--total-scale-factor', String(PDF_SCALE));
    const canvas = globalThis.document.createElement('canvas');
    const ratio = globalThis.devicePixelRatio || 1;
    canvas.width = Math.floor(viewport.width * ratio);
    canvas.height = Math.floor(viewport.height * ratio);
    canvas.setAttribute('aria-hidden', 'true');
    holder.append(canvas);
    container.append(holder);
    // Alan görünümleri HTML katmanında; tuvalde çizilmez (`ENABLE_FORMS`)
    await page.render({
      canvas,
      viewport: page.getViewport({ scale: PDF_SCALE * ratio }),
      annotationMode: pdfjs.AnnotationMode.ENABLE_FORMS,
    }).promise;
    const layer = globalThis.document.createElement('div');
    layer.className = 'annotationLayer';
    holder.append(layer);
    const annotations = await page.getAnnotations({ intent: 'display' });
    const annotationLayer = new pdfjs.AnnotationLayer({
      div: layer,
      accessibilityManager: null,
      annotationCanvasMap: null,
      annotationEditorUIManager: null,
      page,
      viewport: viewport.clone({ dontFlip: true }),
      structTreeLayer: null,
      commentManager: null,
      linkService: LINK_SERVICE,
      annotationStorage: doc.annotationStorage,
    });
    await annotationLayer.render({
      annotations,
      div: layer,
      page,
      viewport: viewport.clone({ dontFlip: true }),
      linkService: LINK_SERVICE as never,
      annotationStorage: doc.annotationStorage,
      imageResourcesPath: '',
      hasJSActions: false,
      fieldObjects: null,
      ...PDF_FORM_RENDER_OPTIONS,
    });
    // Alanın adı (PDF'in `TU` açıklaması ya da alan adı) ekran okuyucuya: pdf.js yalnız kabın
    // `title`'ına yazar; giriş etiketsiz kalırdı (axe "label")
    for (const field of layer.querySelectorAll<HTMLElement>('input, textarea, select')) {
      if (field.getAttribute('aria-label')) continue;
      const name =
        field.closest('section')?.getAttribute('title') || field.getAttribute('name') || '';
      if (name) field.setAttribute('aria-label', name);
    }
  }
}

/**
 * Fill in app (ADR 0042 §19; plan M8b Task 17): şablon PDF'in AcroForm alanları uygulamanın
 * içinde doldurulur; betik ve XFA kapalı. "Save to record" `saveDocument()` baytlarını kayda
 * `pdf_form` kaynağıyla ekler; düzleştirme yok. Office gerekmez (ADR 0024 #20).
 */
export function PdfFormFiller({
  opened,
  url,
  title,
  onClose,
  onSave,
  loadPdfjs = defaultLoader,
}: PdfFormFillerProps) {
  const { t } = useTranslation();
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<unknown>(null);
  // Sayfa kabı durumda tutulur (ref değil): Mantine penceresi içeriği animasyon karesinden sonra
  // bağlar; PDF ondan önce hazır olursa çizim kap gelince yapılır. Ref'le çizim atlanıyor,
  // pencere boş kalıyor ve "Save to record" boş şablonu kayda ekleyebiliyordu [sorular #520]
  const [pages, setPages] = useState<HTMLDivElement | null>(null);
  // Alanlar çizilmeden kaydetme yok: kayda kullanıcının gördüğü ve doldurduğu belge gider
  const [rendered, setRendered] = useState(false);

  useEffect(() => {
    if (!opened || !url) return;
    let cancelled = false;
    let opened_: PdfFormDocument | null = null;
    setState({ kind: 'loading' });
    setError(null);
    void (async () => {
      try {
        const response = await fetch(url);
        if (!response.ok) throw Object.assign(new Error('READ_FAILED'), { code: 'READ_FAILED' });
        const bytes = new Uint8Array(await response.arrayBuffer());
        const pdfjs = await loadPdfjs();
        const doc = await openPdfForm(pdfjs, bytes);
        opened_ = doc;
        const inspection = await inspectPdfForm(doc);
        if (cancelled) return;
        if (!inspection.fillable) {
          setState({ kind: 'error', code: inspection.reason ?? 'PDF_NO_FIELDS' });
          return;
        }
        setState({ kind: 'ready', doc, pdfjs });
      } catch (caught) {
        if (cancelled) return;
        const code =
          typeof caught === 'object' && caught !== null && 'code' in caught
            ? String((caught as { code: unknown }).code)
            : 'READ_FAILED';
        setState({ kind: 'error', code });
      }
    })();
    return () => {
      cancelled = true;
      void opened_?.loadingTask.destroy();
    };
  }, [opened, url, loadPdfjs]);

  useEffect(() => {
    setRendered(false);
    if (state.kind !== 'ready' || !pages) return;
    let cancelled = false;
    renderPages(pages, state.pdfjs, state.doc, () => cancelled)
      .then(() => {
        if (!cancelled) setRendered(true);
      })
      .catch(() => {
        if (!cancelled) setState({ kind: 'error', code: 'READ_FAILED' });
      });
    return () => {
      cancelled = true;
    };
  }, [state, pages]);

  const save = async () => {
    if (state.kind !== 'ready' || !rendered) return;
    setSaving(true);
    setError(null);
    try {
      await onSave(await savePdfForm(state.doc));
    } catch (caught) {
      setError(caught);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      fullScreen
      title={title}
      closeButtonProps={{ 'aria-label': t('forms.fill.close') }}
      classNames={{ body: classes.fillerBody }}
    >
      <div className={classes.fillerBar}>
        <Text size="sm" c="dimmed">
          {t('forms.fill.hint')}
        </Text>
        <Group gap="sm">
          <Button variant="default" onClick={onClose}>
            {t('forms.fill.cancel')}
          </Button>
          <Button
            leftSection={<IconDeviceFloppy size={16} />}
            loading={saving}
            disabled={state.kind !== 'ready' || !rendered}
            onClick={() => void save()}
          >
            {t('forms.fill.save')}
          </Button>
        </Group>
      </div>
      {error ? (
        <Alert color="red" icon={<IconAlertTriangle size={16} />} role="alert">
          {formErrorText(t, error)}
        </Alert>
      ) : null}
      {state.kind === 'loading' || (state.kind === 'ready' && !rendered) ? (
        <div className={classes.fillerStatus} role="status">
          <Loader size="sm" />
          <span>{t('forms.fill.loading')}</span>
        </div>
      ) : null}
      {state.kind === 'error' ? (
        <Alert color="red" icon={<IconAlertTriangle size={16} />} role="alert">
          {formErrorText(t, { code: state.code })}
        </Alert>
      ) : null}
      <div ref={setPages} className={classes.fillerPages} data-pdf-form />
    </Modal>
  );
}
